class NewsletterForm extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.form = this.querySelector('form');
    this.submitButton = this.querySelector('button[type="submit"]');
    this.emailInput = this.querySelector('input[type="email"]');
    
    if (this.form) {
      // Remove any existing submit event listeners by cloning the form
      // This ensures we have full control over the submission
      
      // Prevent default form submission with capture phase
      this.form.addEventListener('submit', this.handleSubmit.bind(this), true);
      
      // Also prevent in bubble phase as backup
      this.form.addEventListener('submit', this.handleSubmit.bind(this), false);
      
      // Mark form as AJAX-enabled
      this.form.setAttribute('data-ajax-submit', 'true');
      
      // Set up hCaptcha callback
      this.setupCaptchaCallback();
    }
  }

  setupCaptchaCallback() {
    // Store reference to this element
    const newsletterForm = this;
    
    // Wait for hCaptcha to be ready
    const checkHcaptcha = () => {
      const hcaptchaWidget = this.form.querySelector('.h-captcha');
      if (!hcaptchaWidget) return;
      
      console.log('hCaptcha widget found, setting up...');
      
      // Create a unique callback name for this form
      const callbackName = `hcaptchaCallback_${Math.random().toString(36).substr(2, 9)}`;
      
      // Store the callback globally
      window[callbackName] = function(token) {
        console.log('hCaptcha completed, token received');
        // Mark that we have the token and reset executing flag
        newsletterForm.captchaCompleted = true;
        newsletterForm.captchaExecuting = false;
        
        // Remove loading state before re-submitting
        if (newsletterForm.submitButton) {
          newsletterForm.submitButton.classList.remove('loading');
          newsletterForm.submitButton.removeAttribute('disabled');
          newsletterForm.submitButton.textContent = 'Submitting...';
        }
        
        // Auto-submit the form now that CAPTCHA is complete
        setTimeout(() => {
          const submitEvent = new Event('submit', { bubbles: true, cancelable: true });
          newsletterForm.form.dispatchEvent(submitEvent);
        }, 100);
      };
      
      // Try to set the callback attribute
      hcaptchaWidget.setAttribute('data-callback', callbackName);
      
      console.log('hCaptcha callback set:', callbackName);
    };
    
    // Try multiple times as hCaptcha might load async
    setTimeout(checkHcaptcha, 100);
    setTimeout(checkHcaptcha, 500);
    setTimeout(checkHcaptcha, 1000);
    setTimeout(checkHcaptcha, 2000);
  }

  handleSubmit(event) {
    console.log('Newsletter form submit intercepted');
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    
    if (this.submitButton && this.submitButton.classList.contains('loading')) {
      console.log('Already submitting, ignoring');
      return false;
    }
    
    // Check for hCaptcha or reCAPTCHA
    const hcaptchaWidget = this.form.querySelector('.h-captcha');
    const hcaptchaResponse = this.form.querySelector('[name="h-captcha-response"]');
    const gRecaptchaResponse = this.form.querySelector('[name="g-recaptcha-response"]');
    
    // If CAPTCHA widget exists but hasn't been completed yet
    if (hcaptchaWidget && (!hcaptchaResponse || !hcaptchaResponse.value)) {
      console.log('hCaptcha required but not completed yet...');
      
      // Check if we need to execute the CAPTCHA
      if (!this.captchaExecuting) {
        this.captchaExecuting = true;
        console.log('Executing hCaptcha...');
        
        // Add loading state
        if (this.submitButton) {
          this.submitButton.classList.add('loading');
          this.submitButton.setAttribute('disabled', 'disabled');
          const originalButtonText = this.submitButton.textContent;
          this.submitButton.textContent = 'Verifying...';
          this.submitButton.dataset.originalText = originalButtonText;
        }
        
        // Try to execute invisible CAPTCHA
        if (window.hcaptcha) {
          try {
            // Find all h-captcha widgets and get the one in our form
            const widgets = document.querySelectorAll('.h-captcha');
            widgets.forEach((widget, index) => {
              if (this.form.contains(widget)) {
                console.log(`Executing hCaptcha widget ${index}...`);
                window.hcaptcha.execute(index);
              }
            });
          } catch (e) {
            console.error('Error executing hCaptcha:', e);
            // Reset state and show error
            this.captchaExecuting = false;
            if (this.submitButton) {
              this.submitButton.classList.remove('loading');
              this.submitButton.removeAttribute('disabled');
              this.submitButton.textContent = this.submitButton.dataset.originalText || 'Sign Up';
            }
            this.showErrorMessage('CAPTCHA verification failed. Please try again.');
          }
        }
      }
      
      // Don't proceed with form submission yet - wait for CAPTCHA callback
      return false;
    }
    
    // If we get here, either CAPTCHA is not required or it's been completed
    console.log('CAPTCHA check passed, proceeding with submission...');
    this.captchaExecuting = false;
    this.captchaCompleted = false;
    
    // Add loading state
    if (this.submitButton) {
      this.submitButton.classList.add('loading');
      this.submitButton.setAttribute('disabled', 'disabled');
      const originalButtonText = this.submitButton.textContent;
      this.submitButton.textContent = 'Submitting...';
      this.submitButton.dataset.originalText = originalButtonText;
    }
    
    // Clear any existing messages
    this.clearMessages();
    
    const formData = new FormData(this.form);
    
    console.log('Submitting form data via AJAX...');
    
    // Use XMLHttpRequest for better compatibility with Shopify forms
    const xhr = new XMLHttpRequest();
    xhr.open('POST', this.form.action || '/contact#contact_form', true);
    xhr.setRequestHeader('Accept', 'application/json, text/javascript, */*; q=0.01');
    xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
    
    xhr.onload = () => {
      console.log('XHR response received:', xhr.status);
      // Remove loading state
      if (this.submitButton) {
        this.submitButton.classList.remove('loading');
        this.submitButton.removeAttribute('disabled');
        this.submitButton.textContent = this.submitButton.dataset.originalText || 'Sign Up';
      }
      
      if (xhr.status >= 200 && xhr.status < 400) {
        // Success - show message and clear form
        console.log('Form submitted successfully');
        this.showSuccessMessage();
        if (this.emailInput) {
          this.emailInput.value = '';
        }
        
        // Reset CAPTCHA if present
        if (window.hcaptcha) {
          const widgetId = this.form.querySelector('.h-captcha')?.dataset.widgetId;
          if (widgetId !== undefined) {
            window.hcaptcha.reset(widgetId);
          }
        }
        if (window.grecaptcha) {
          const widgetId = this.form.querySelector('.g-recaptcha')?.dataset.widgetId;
          if (widgetId !== undefined) {
            window.grecaptcha.reset(widgetId);
          }
        }
      } else {
        // Server error
        console.error('Server error:', xhr.status, xhr.responseText);
        
        // Handle specific error codes
        if (xhr.status === 429) {
          this.showErrorMessage('Too many requests. Please wait a moment and try again.');
        } else {
          // Parse error message
          try {
            const errorData = JSON.parse(xhr.responseText);
            if (errorData.errors) {
              this.showErrorMessage(errorData.errors);
            } else {
              this.showErrorMessage('Unable to subscribe. Please try again.');
            }
          } catch (e) {
            this.showErrorMessage('Unable to subscribe. Please try again.');
          }
        }
      }
    };
    
    xhr.onerror = () => {
      console.error('XHR network error');
      // Remove loading state
      if (this.submitButton) {
        this.submitButton.classList.remove('loading');
        this.submitButton.removeAttribute('disabled');
        this.submitButton.textContent = this.submitButton.dataset.originalText || 'Sign Up';
      }
      
      this.showErrorMessage('Network error. Please check your connection.');
    };
    
    xhr.send(formData);
    
    // Return false to ensure form doesn't submit
    return false;
  }

  clearMessages() {
    const existingMessages = this.querySelectorAll('.newsletter-form__message, .form-message');
    existingMessages.forEach(msg => msg.remove());
  }

  showSuccessMessage() {
    // Create circular toast notification
    const toast = document.createElement('div');
    toast.className = 'newsletter-form-toast newsletter-form-toast--success';
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    
    toast.innerHTML = `
      <div class="newsletter-form-toast__circle">
        <div class="newsletter-form-toast__text">Thank you for subscribing!</div>
      </div>
    `;
    
    // Add styles if not already present
    this.injectToastStyles();
    
    // Make parent container relative
    const container = this.closest('.newsletter, .footer-block__newsletter, .page-width');
    if (container) {
      container.style.position = 'relative';
      container.appendChild(toast);
    } else {
      this.style.position = 'relative';
      this.appendChild(toast);
    }
    
    // Trigger animation
    setTimeout(() => toast.classList.add('newsletter-form-toast--visible'), 10);
    
    // Auto-remove after 5 seconds and clear form
    setTimeout(() => {
      toast.classList.remove('newsletter-form-toast--visible');
      setTimeout(() => {
        toast.remove();
        this.form.reset();
      }, 300);
    }, 5000);
  }
  
  // TEST FUNCTION - Remove after testing
  testSuccessMessage() {
    this.clearMessages();
    this.showSuccessMessage();
  }
  
  testErrorMessage() {
    this.clearMessages();
    this.showErrorMessage('This is a test error message.');
  }

  showErrorMessage(message) {
    // Create circular toast notification
    const toast = document.createElement('div');
    toast.className = 'newsletter-form-toast newsletter-form-toast--error';
    toast.setAttribute('role', 'alert');
    toast.setAttribute('aria-live', 'assertive');
    
    toast.innerHTML = `
      <div class="newsletter-form-toast__circle">
        <div class="newsletter-form-toast__text">Oops! ${message}</div>
      </div>
    `;
    
    // Add styles if not already present
    this.injectToastStyles();
    
    // Make parent container relative
    const container = this.closest('.newsletter, .footer-block__newsletter, .page-width');
    if (container) {
      container.style.position = 'relative';
      container.appendChild(toast);
    } else {
      this.style.position = 'relative';
      this.appendChild(toast);
    }
    
    // Trigger animation
    setTimeout(() => toast.classList.add('newsletter-form-toast--visible'), 10);
    
    // Auto-remove after 5 seconds
    setTimeout(() => {
      toast.classList.remove('newsletter-form-toast--visible');
      setTimeout(() => toast.remove(), 300);
    }, 5000);
  }
  
  injectToastStyles() {
    // Only inject once
    if (document.getElementById('newsletter-form-toast-styles')) return;
    
    const style = document.createElement('style');
    style.id = 'newsletter-form-toast-styles';
    style.textContent = `
      .newsletter-form-toast {
        position: absolute;
        bottom: -40px;
        right: 20px;
        z-index: 100;
        transform: scale(0) translateY(50px);
        opacity: 0;
        transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
      }
      
      .newsletter-form-toast--visible {
        transform: scale(1) translateY(0);
        opacity: 1;
      }
      
      .newsletter-form-toast__circle {
        width: 180px;
        height: 180px;
        border-radius: 50%;
        background: #000000;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 30px;
        box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
        animation: popBounce 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
      }
      
      .newsletter-form-toast--error .newsletter-form-toast__circle {
        background: #dc2626;
      }
      
      .newsletter-form-toast__text {
        color: white;
        font-size: 17px;
        font-weight: 600;
        line-height: 1.4;
        text-align: center;
        word-wrap: break-word;
      }
      
      @keyframes popBounce {
        0% {
          transform: scale(0) rotate(-180deg);
        }
        50% {
          transform: scale(1.1) rotate(10deg);
        }
        100% {
          transform: scale(1) rotate(0deg);
        }
      }
      
      @media (max-width: 768px) {
        .newsletter-form-toast {
          position: fixed;
          bottom: 30px;
          left: 50%;
          right: auto;
          transform: translateX(-50%) scale(0) translateY(50px);
        }
        
        .newsletter-form-toast--visible {
          transform: translateX(-50%) scale(1) translateY(0);
        }
        
        .newsletter-form-toast__circle {
          width: 150px;
          height: 150px;
          padding: 25px;
        }
        
        .newsletter-form-toast__text {
          font-size: 15px;
        }
      }
      
      @media (max-width: 480px) {
        .newsletter-form-toast {
          position: fixed;
          bottom: 20px;
          left: 50%;
          right: auto;
          transform: translateX(-50%) scale(0) translateY(50px);
        }
        
        .newsletter-form-toast--visible {
          transform: translateX(-50%) scale(1) translateY(0);
        }
        
        .newsletter-form-toast__circle {
          width: 130px;
          height: 130px;
          padding: 20px;
        }
        
        .newsletter-form-toast__text {
          font-size: 14px;
        }
      }
    `;
    document.head.appendChild(style);
  }
}

customElements.define('newsletter-form', NewsletterForm);
